/**
 * RegisterFormPreview — replica of the PUBLIC registration funnel for the
 * Form Fields "Live Preview": apps/app ContestTeamDetails (team step,
 * :860-1058) + ContestPlayerDetails/PlayerListFields (players step) +
 * RegistrationTournament/FormField (DaisyUI fieldset/legend/input/select).
 * Keep in sync with those files — this is what "Live Preview" promises the
 * admin. Styling lives in the `rgp-*` block of BrowserPreview.css.
 *
 * Everything is inert (disabled inputs, dead buttons). The team step also
 * renders the BUILT-IN Team Name (hidden when `excludeTeamName` — reactive
 * to the "Require team name" toggle) and Division select, so it is never
 * empty; the coach/manager block and SMS consent are deliberately NOT
 * replicated (owner decision 2026-09-29 — a footnote outside the frame
 * covers them).
 */
import { FileText, Plus } from 'lucide-react';
import { isIndividualSport } from '@staty/shared/utils/sportCategory';

export interface RegisterPreviewField {
  fieldId?: string;
  name?: string;
  required?: boolean;
  helperText?: string;
  placeholder?: string;
  fieldType?: string;
  options?: string[];
  order?: number;
}

export interface RegisterPreviewProps {
  fields?: RegisterPreviewField[];
  type?: 'player' | 'team';
  sport?: string;
  registrationName?: string;
  excludeTeamName?: boolean;
  minPlayers?: number;
  maxPlayers?: number;
  emptyMessage?: string;
}

// Replica of apps/app RegistrationTournament/FormField.tsx per-type branches.
function PreviewFormField({ field, colSpanFull = false }: { field: RegisterPreviewField; colSpanFull?: boolean }) {
  const required = !!field.required;
  const input = (() => {
    switch (field.fieldType) {
      case 'select':
        return (
          <select className="rgp-select" disabled>
            {/* The public FormField nests <em>Select</em>; browsers render
                option children as plain text, so plain text here matches. */}
            <option value="">Select</option>
            {(field.options || []).map((opt) => <option key={opt} value={opt}>{opt}</option>)}
          </select>
        );
      case 'phone':
        return <input type="tel" className="rgp-input" placeholder="(XXX) XXX-XXXX" maxLength={14} disabled />;
      case 'email':
        return <input type="email" className="rgp-input" placeholder="name@example.com" disabled />;
      case 'jersey':
        return <input type="text" className="rgp-input" placeholder="00" maxLength={2} inputMode="numeric" disabled />;
      case 'date':
        return <input type="date" className="rgp-input" disabled />;
      case 'textarea':
        return <textarea className="rgp-textarea" placeholder={field.placeholder || ''} disabled />;
      default:
        return <input type="text" className="rgp-input" placeholder={field.placeholder || ''} disabled />;
    }
  })();
  const spanClass = colSpanFull || field.fieldType === 'textarea' ? ' rgp-col-full' : '';
  return (
    <div className={`rgp-field${spanClass}`}>
      <fieldset className="rgp-fieldset">
        <legend className="rgp-legend">
          {field.name}
          {required ? ' *' : ''}
        </legend>
        {input}
        {field.helperText ? <p className="rgp-helper">{field.helperText}</p> : null}
      </fieldset>
    </div>
  );
}

const byOrder = (a: RegisterPreviewField, b: RegisterPreviewField) => (a.order ?? 0) - (b.order ?? 0);

function TeamStep({ fields = [], sport, registrationName, excludeTeamName }: RegisterPreviewProps) {
  const heading = registrationName && registrationName !== 'Registration'
    ? `Register for the ${registrationName}`
    : 'Register';
  return (
    <div className="rgp-page">
      <h2 className="rgp-h2">
        {heading}
        {' '}
        {sport ? <span className="rgp-sport">{sport}</span> : null}
      </h2>
      {sport && isIndividualSport(sport) ? (
        <label className="rgp-checkbox-row">
          <input type="checkbox" checked disabled readOnly />
          <span>Auto-populate your player information</span>
        </label>
      ) : null}
      <div className="rgp-grid">
        {!excludeTeamName && (
          <PreviewFormField
            field={{ fieldId: 'teamName', name: 'Team Name', required: true, placeholder: 'Enter your team name' }}
            colSpanFull
          />
        )}
        <PreviewFormField
          field={{ fieldId: 'requestedDivision', name: 'Division', required: true, fieldType: 'select' }}
          colSpanFull
        />
        {[...fields].sort(byOrder).map((f, i) => (
          <PreviewFormField key={f.fieldId || i} field={f} colSpanFull />
        ))}
      </div>
      <button type="button" className="rgp-btn-primary" disabled>Add Team</button>
    </div>
  );
}

function PlayersStep({ fields = [], sport, minPlayers, maxPlayers, emptyMessage }: RegisterPreviewProps) {
  if (!fields.length) {
    return (
      <div className="preview-empty-state">
        <FileText />
        <p>{emptyMessage}</p>
      </div>
    );
  }
  const min = Number(minPlayers) || 1;
  const hasMax = Number(maxPlayers) > 0;
  const max = Number(maxPlayers) || 0;
  // Heading annotation — same branches as ContestPlayerDetails' <h2>.
  const annotation = (min === 1 && (!hasMax || max === 1))
    ? null
    : !hasMax
      ? `${min}+ players`
      : min === max
        ? `${min} player${min === 1 ? '' : 's'} per team`
        : `${min} Required / ${max} Max`;
  const addLabel = sport && isIndividualSport(sport) ? 'Add Partner' : 'Add Players';
  return (
    <div className="rgp-page">
      <h2 className="rgp-h2">
        Player Details
        {annotation ? ` (${annotation})` : ''}
      </h2>
      <div className="rgp-player-card">
        <div className="rgp-player-head">
          <h4 className="rgp-h4">Player #1</h4>
          <button type="button" className="rgp-remove" disabled>Remove</button>
        </div>
        <div className="rgp-grid rgp-grid-players">
          {[...fields].sort(byOrder).map((f, i) => (
            <PreviewFormField key={f.fieldId || i} field={f} />
          ))}
        </div>
      </div>
      <button type="button" className="rgp-btn-default" disabled>
        <Plus className="w-4 h-4" />
        {addLabel}
      </button>
      <div className="rgp-footer">
        <button type="button" className="rgp-btn-ghost" disabled>Previous</button>
        <button type="button" className="rgp-btn-primary" disabled>Next</button>
      </div>
    </div>
  );
}

export default function RegisterFormPreview(props: RegisterPreviewProps) {
  return props.type === 'team' ? <TeamStep {...props} /> : <PlayersStep {...props} />;
}
